import { Dispatch, SetStateAction, useMemo } from "react";
import { Form, GENERATE_TRIGGER_KEY, Input } from "domains/3d/interfaces";
import Section from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import Divider from "domains/ui/components/Divider";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";

import { HStack, Skeleton, VStack } from "@chakra-ui/react";

import GenericInput from "./GenericInput";
import GenericTriggerButton, {
  GenericTriggerButtonProps,
} from "./GenericTriggerButton";

interface GenericFormDisplayProps {
  form: Form;
  setForm: Dispatch<SetStateAction<Form>>;
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  triggerButtonProps: Omit<
    GenericTriggerButtonProps,
    "label" | "absolutePosition"
  > & {
    label?: string;
  };
  type: "3d" | "video";
  disabledReasons: string[];
  isLoadingModel: boolean;
}

export default function GenericFormDisplay({
  form,
  setForm,
  model,
  triggerButtonProps,
  type,
  disabledReasons,
  isLoadingModel,
}: GenericFormDisplayProps) {
  const generateTrigger = model?.uiConfig?.triggerGenerate;

  const sectionInputs = useMemo(() => {
    const sections: {
      key: string;
      label: string;
      isWithTitle: boolean;
      inputs: Input[];
    }[] = [];
    let canPasteImage = true;
    let wasTriggerAdded = false;

    model?.inputs?.forEach((input) => {
      if (
        input.type === "file" ||
        input.type === "file_array" ||
        input.prompt
      ) {
        let isWithPasteImage = false;
        if (canPasteImage && input.type === "file") {
          const value = form[input.name];
          isWithPasteImage = !value;
        } else if (canPasteImage && input.type === "file_array") {
          const value = form[input.name];
          if (value) {
            isWithPasteImage =
              Array.isArray(value) && value.length < (input.minLength ?? 10);
          }
        }
        sections.push({
          key: input.name,
          label: input.label ?? input.name,
          isWithTitle: true,
          inputs: [
            {
              ...input,
              isWithPasteImage,
            },
          ],
        });
        if (isWithPasteImage) {
          canPasteImage = false;
        }
      } else {
        const group = input.group;
        if (group) {
          const section = sections.find((s) => s.key === group);
          if (section) {
            section.inputs.push(input);
          } else {
            sections.push({
              key: group,
              label: group,
              isWithTitle: true,
              inputs: [input],
            });
          }
        } else {
          const lastSection = sections[sections.length - 1];
          if (lastSection && !lastSection.isWithTitle) {
            lastSection.inputs.push(input);
          } else {
            sections.push({
              key: input.name,
              label: input.label ?? input.name,
              isWithTitle: false,
              inputs: [input],
            });
          }
        }
      }

      if (
        generateTrigger &&
        !wasTriggerAdded &&
        generateTrigger.after === input.name
      ) {
        sections.push({
          key: GENERATE_TRIGGER_KEY,
          label:
            generateTrigger.label ?? triggerButtonProps.label ?? "Generate",
          isWithTitle: true,
          inputs: [],
        });
        wasTriggerAdded = true;
      }
    });

    if (!wasTriggerAdded) {
      sections.push({
        key: GENERATE_TRIGGER_KEY + "Absolute",
        label: generateTrigger?.label ?? triggerButtonProps.label ?? "Generate",
        isWithTitle: true,
        inputs: [],
      });
    }

    return sections;
  }, [model, form, generateTrigger, triggerButtonProps.label]);

  if (isLoadingModel) {
    return (
      <Section>
        <VStack align="stretch">
          <HStack justify="space-between">
            <Skeleton w="150px" h="24px" />
            <Skeleton w="50px" h="24px" />
          </HStack>
          <Skeleton w="100%" h="150px" />
        </VStack>
      </Section>
    );
  }

  if (!model) {
    return null;
  }

  return (
    <>
      {sectionInputs.map((section) => {
        if (section.key.startsWith(GENERATE_TRIGGER_KEY)) {
          return (
            <GenericTriggerButton
              key={section.key}
              {...triggerButtonProps}
              isAbsolute={section.key.endsWith("Absolute")}
              label={section.label}
              disabledTooltip={
                <DisabledTooltip disabledReasons={disabledReasons} />
              }
            />
          );
        }

        if (
          section.inputs[0] &&
          (section.inputs[0].type === "file" ||
            section.inputs[0].type === "file_array" ||
            section.inputs[0].prompt)
        ) {
          return (
            <>
              <GenericInput
                key={section.inputs[0].name}
                input={section.inputs[0]}
                model={model}
                form={form}
                setForm={setForm}
                type={type}
              />
              <Divider />
            </>
          );
        }

        return (
          <>
            <Section
              key={section.key}
              headerProps={
                section.isWithTitle
                  ? {
                      id: section.key,
                      title: section.label,
                    }
                  : undefined
              }
            >
              <VStack align="stretch">
                {section.inputs.map((input) => {
                  return (
                    <GenericInput
                      key={input.name}
                      model={model}
                      input={input}
                      form={form}
                      setForm={setForm}
                      type={type}
                    />
                  );
                })}
              </VStack>
            </Section>
            <Divider />
          </>
        );
      })}
    </>
  );
}

function DisabledTooltip({ disabledReasons }: { disabledReasons: string[] }) {
  if (!disabledReasons.length) {
    return null;
  }

  return (
    <span>
      {disabledReasons.map((reason) => (
        <span key={reason} style={{ display: "block" }}>
          {reason}
        </span>
      ))}
    </span>
  );
}
